import { useEffect, useState } from 'react'
import { adminApi } from '../../api'
import type { AdminUser } from '../../api/types'
import { useToast } from '../../context/ToastContext'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Toggle } from '../../components/ui/Toggle'
import { Spinner } from '../../components/ui/Spinner'

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const toast = useToast()

  useEffect(() => {
    void load()
  }, [])

  async function load() {
    try {
      const { users } = await adminApi.listUsers()
      setUsers(users)
    } catch {
      toast.push('Benutzer konnten nicht geladen werden', 'error')
    }
  }

  async function remove(u: AdminUser) {
    if (!window.confirm(`${u.email} löschen?`)) return
    try {
      await adminApi.deleteUser(u.id)
      toast.push('Benutzer gelöscht', 'success')
      void load()
    } catch {
      toast.push('Löschen fehlgeschlagen', 'error')
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Benutzer</h1>
        <Button onClick={() => setCreating(true)}>+ Benutzer</Button>
      </div>

      {users === null ? (
        <Spinner />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Quelle</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.displayName}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`tag ${u.role === 'ADMIN' ? 'tag-accent' : ''}`}>{u.role}</span>
                </td>
                <td>{u.oidcIssuer ? 'SSO' : 'Lokal'}</td>
                <td>{u.disabled ? 'Deaktiviert' : 'Aktiv'}</td>
                <td className="row-actions">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => remove(u)}>
                    Löschen
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <UserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false)
          void load()
        }}
      />
      <UserDialog
        open={!!editing}
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          void load()
        }}
      />
    </div>
  )
}

function UserDialog({
  open,
  user,
  onClose,
  onSaved,
}: {
  open: boolean
  user?: AdminUser | null
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'ADMIN' | 'USER'>('USER')
  const [disabled, setDisabled] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setEmail(user?.email || '')
    setDisplayName(user?.displayName || '')
    setPassword('')
    setRole(user?.role || 'USER')
    setDisabled(user?.disabled || false)
  }, [open, user])

  async function save() {
    setSubmitting(true)
    try {
      if (user) {
        await adminApi.updateUser(user.id, {
          displayName,
          role,
          disabled,
          password: password || undefined,
        })
      } else {
        await adminApi.createUser({
          email,
          displayName,
          password: password || undefined,
          role,
        })
      }
      toast.push('Gespeichert', 'success')
      onSaved()
    } catch {
      toast.push('Speichern fehlgeschlagen', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={user ? 'Benutzer bearbeiten' : 'Benutzer erstellen'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={save} loading={submitting}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="stack">
        <Input label="Anzeigename" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        <Input
          type="email"
          label="E-Mail"
          value={email}
          disabled={!!user}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          type="password"
          label={user ? 'Neues Passwort (leer = unverändert)' : 'Passwort'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Select
          label="Rolle"
          value={role}
          onValueChange={(v) => setRole(v === 'ADMIN' ? 'ADMIN' : 'USER')}
          options={[
            { value: 'USER', label: 'Benutzer' },
            { value: 'ADMIN', label: 'Administrator' },
          ]}
        />
        {user ? (
          <Toggle label="Deaktiviert" checked={disabled} onCheckedChange={setDisabled} />
        ) : null}
      </div>
    </Dialog>
  )
}
